import { CmaasApiError, type ApiErrorBody, type Page } from '@cmaas/api/sdk';

export type { Page };
export { CmaasApiError };

// The admin-portal client (P5c plan): unlike packages/api/sdk (built around
// one static machine-key apiKey) and packages/portal's client (tenant_id +
// portal-role claims), this holds a rotating short-lived admin-user access
// token in memory and transparently refreshes it via the HttpOnly cookie on
// a 401. Never persists the access token anywhere (ADR-0004/0005) — a page
// reload always re-derives it from AuthProvider's boot-time refresh. No
// tenant_id dimension anywhere: admin_users.email is globally unique
// (docs/P5-NOTES.md D-P5a-9's tenant_id requirement is portal-only).

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3000';

let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** AuthProvider registers this so the client can react to a failed refresh without importing React state. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export interface AdminAuthResponse {
  access_token: string;
  expires_in: number;
  user: { id: string; email: string; name: string };
}

async function rawFetch(path: string, init: RequestInit): Promise<Response> {
  return fetch(new URL(path, BASE_URL), init);
}

let inFlightRefresh: Promise<AdminAuthResponse | null> | null = null;

/**
 * Credentials-included: the ONLY calls that need the HttpOnly refresh cookie.
 * Single-flight: the refresh token rotates on use and a replay revokes the whole
 * session family, so concurrent callers (StrictMode's double boot effect,
 * parallel 401s) must share one request rather than each presenting the cookie.
 */
function refreshSession(): Promise<AdminAuthResponse | null> {
  inFlightRefresh ??= (async () => {
    try {
      const res = await rawFetch('/v1/admin/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      return (await res.json()) as AdminAuthResponse;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

export async function adminLogin(email: string, password: string): Promise<AdminAuthResponse> {
  const res = await rawFetch('/v1/admin/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json().catch(() => ({}))) as AdminAuthResponse & Partial<ApiErrorBody>;
  if (!res.ok) throw new CmaasApiError(res.status, body as ApiErrorBody);
  return body;
}

export async function adminLogout(): Promise<void> {
  await rawFetch('/v1/admin/auth/logout', { method: 'POST', credentials: 'include' });
}

/** Boot-time session restore: silently succeeds/fails, never throws. */
export async function restoreSession(): Promise<AdminAuthResponse | null> {
  try {
    return await refreshSession();
  } catch {
    return null;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

/**
 * Every authenticated call. On a 401 (expired access token — the common
 * case, since it's a 15-minute token), tries ONE silent refresh via the
 * cookie, retries the original request once, then gives up: clears the
 * token and calls the session-expired handler so AuthProvider can redirect
 * to /login rather than the caller having to know about refresh at all.
 */
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const attempt = async (): Promise<Response> => {
    const url = new URL(path, BASE_URL);
    for (const [key, value] of Object.entries(opts.query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
    return rawFetch(url.pathname + url.search, {
      method: opts.method ?? 'GET',
      headers: {
        ...(accessToken && { authorization: `Bearer ${accessToken}` }),
        ...(opts.body !== undefined && { 'content-type': 'application/json' }),
      },
      ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
    });
  };

  let res = await attempt();
  if (res.status === 401 && accessToken) {
    const refreshed = await refreshSession();
    if (refreshed) {
      accessToken = refreshed.access_token;
      res = await attempt();
    } else {
      accessToken = null;
      onSessionExpired?.();
    }
  }

  const body = (await res.json().catch(() => ({}))) as T & Partial<ApiErrorBody>;
  if (!res.ok) throw new CmaasApiError(res.status, body as ApiErrorBody);
  return body;
}
